import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { adminKycAPI } from '../../../services/api';
import { toast } from 'react-toastify';
import { FileText, Plus, Search, Copy, Eye, ExternalLink, CheckCircle, Clock, XCircle, AlertCircle, SlidersHorizontal, RotateCcw } from 'lucide-react';
import ActionMenu from '../../shared/ActionMenu';

const C = {
  bg: '#f4f4f9', surface: '#FFFFFF', border: '#e8e8f1',
  ink: '#14142b', body: '#4a4a6a', muted: '#6e6e86',
  primary: '#7c3aed', primarySoft: '#f1ebff', primaryTint: '#ddd6fe',
  success: '#10B981', successSoft: '#D1FAE5',
  danger: '#EF4444', dangerSoft: '#FEE2E2',
  warning: '#F59E0B', warnSoft: '#FEF3C7',
  info: '#06B6D4', infoSoft: '#E0F9FF',
  radius: 14, radiusSm: 8,
};

const KpiCard = ({ label, value, sub, color, bg, Icon, active, onClick }) => {
  const [hov, setHov] = useState(false);
  return (
    <div onClick={onClick}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{
        background: active ? color + '12' : C.surface,
        border: `1.5px solid ${active ? color : hov ? color + '55' : C.border}`,
        borderRadius: C.radius, padding: '18px 20px',
        cursor: onClick ? 'pointer' : 'default', transition: 'all .15s',
        boxShadow: active ? `0 4px 20px ${color}20` : '0 1px 4px rgba(0,0,0,.04)',
        display: 'flex', flexDirection: 'column', gap: 10,
      }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: .5, marginBottom: 6 }}>{label}</div>
          <div style={{ fontSize: 26, fontWeight: 800, color: active ? color : C.ink, lineHeight: 1, minHeight: 30, display: 'flex', alignItems: 'center' }}>{value}</div>
        </div>
        {Icon && (
          <div style={{ width: 38, height: 38, borderRadius: 10, background: active ? color : bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'background .15s' }}>
            <Icon size={17} color={active ? '#fff' : color} />
          </div>
        )}
      </div>
      {sub && <div style={{ fontSize: 12, color: C.muted }}>{sub}</div>}
    </div>
  );
};

const ALL_GROUPS = {
  'Document KYC':               ['aadhaar_front','aadhaar_back','pan_card','selfie','mobile','email_id','address'],
  'Business KYC':               ['gst_certificate','udyam_registration'],
  'Bank Verification':          ['cancelled_cheque','bank_statement','account_holder_name','account_number','ifsc_code'],
  'Website / App Verification': ['website_url','product_details','ad_platform'],
  'Declarations':               ['digital_signature','terms_acceptance'],
};

const FIELD_LABELS = {
  aadhaar_front:'Aadhaar Card (Front)', aadhaar_back:'Aadhaar Card (Back)',
  pan_card:'PAN Card', selfie:'Selfie / Live Photo', mobile:'Mobile Number',
  email_id:'Email ID', address:'Current Address',
  gst_certificate:'GST Certificate (Optional)', udyam_registration:'Udyam Registration (Optional)',
  cancelled_cheque:'Cancelled Cheque', bank_statement:'Bank Statement (3–6 Months)',
  account_holder_name:'Account Holder Name', account_number:'Account Number', ifsc_code:'IFSC Code',
  website_url:'Website URL / App', product_details:'Product / Service Details', ad_platform:'Advertisement Platform',
  digital_signature:'Digital Signature', terms_acceptance:'Terms Acceptance',
};

const STATUS_META = {
  active:    { color: C.info,    bg: '#EFF9FF', icon: Clock,        label: 'Active'    },
  submitted: { color: C.warning, bg: '#FFFBEB', icon: AlertCircle,  label: 'Submitted' },
  approved:  { color: C.success, bg: '#F0FDF4', icon: CheckCircle,  label: 'Approved'  },
  rejected:  { color: C.danger,  bg: '#FEF2F2', icon: XCircle,      label: 'Rejected'  },
};

const Badge = ({ status }) => {
  const m = STATUS_META[status] || STATUS_META.active;
  const Icon = m.icon;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      background: m.bg, color: m.color,
      border: `1px solid ${m.color}33`,
      borderRadius: 20, padding: '3px 10px', fontSize: 12, fontWeight: 600,
    }}>
      <Icon size={11} /> {m.label}
    </span>
  );
};

const Btn = ({ onClick, color = C.primary, children, disabled, size = 'md', style = {} }) => (
  <button onClick={onClick} disabled={disabled} style={{
    background: color, color: '#fff', border: 'none',
    borderRadius: size === 'sm' ? 7 : 9,
    padding: size === 'sm' ? '6px 12px' : '9px 18px',
    fontSize: size === 'sm' ? 12 : 13, fontWeight: 600,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.6 : 1, display: 'inline-flex',
    alignItems: 'center', gap: 6, whiteSpace: 'nowrap', ...style,
  }}>{children}</button>
);

/* ─── Create KYC Modal ─── */
const CreateModal = ({ onClose, onCreate }) => {
  const [mid, setMid]           = useState('');
  const [fields, setFields]     = useState([]);
  const [creating, setCreating] = useState(false);
  const [result, setResult]     = useState(null);

  const toggle = (key) => setFields(p => p.includes(key) ? p.filter(k => k !== key) : [...p, key]);
  const toggleGroup = (keys) => setFields(p => {
    const all = keys.every(k => p.includes(k));
    return all ? p.filter(k => !keys.includes(k)) : [...new Set([...p, ...keys])];
  });

  const submit = async () => {
    if (!mid.trim()) return toast.error('MID is required');
    if (fields.length === 0) return toast.error('Select at least one field');
    setCreating(true);
    try {
      const { data } = await adminKycAPI.create({ mid: mid.trim(), fields });
      setResult({ url: data.url, merchant: data.merchant });
      toast.success('KYC form created!');
      onCreate();
    } catch (err) {
      const d = err?.response?.data;
      if (d?.url) {
        setResult({ url: d.url });
        toast.warning('Active form already exists');
      } else {
        toast.error(d?.error || 'Failed to create KYC form');
      }
    } finally { setCreating(false); }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(20,20,43,.55)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', padding: 16,
    }}>
      <div style={{
        background: '#fff', borderRadius: 16, width: '100%', maxWidth: 660,
        maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 24px 64px rgba(0,0,0,.2)',
      }}>
        {/* Header */}
        <div style={{ padding: '20px 24px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: C.ink }}>
            <FileText size={17} style={{ marginRight: 8, verticalAlign: 'middle', color: C.primary }} />
            Create KYC Form
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: C.muted }}>×</button>
        </div>

        <div style={{ padding: '20px 24px 24px' }}>
          {!result ? (
            <>
              {/* MID input */}
              <div style={{ marginBottom: 20 }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: C.body, display: 'block', marginBottom: 6 }}>
                  Merchant MID <span style={{ color: C.danger }}>*</span>
                </label>
                <input
                  value={mid}
                  onChange={e => setMid(e.target.value)}
                  placeholder="e.g. MID1234567"
                  style={{
                    width: '100%', padding: '10px 13px', fontSize: 14,
                    border: `1.5px solid ${C.border}`, borderRadius: 9,
                    outline: 'none', color: C.ink, boxSizing: 'border-box',
                  }}
                />
              </div>

              {/* Field selector */}
              <div style={{ marginBottom: 20 }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: C.body, display: 'block', marginBottom: 12 }}>
                  Select Fields <span style={{ color: C.danger }}>*</span>
                  <span style={{ fontSize: 12, fontWeight: 400, color: C.muted, marginLeft: 8 }}>
                    ({fields.length} selected)
                  </span>
                </label>

                {Object.entries(ALL_GROUPS).map(([group, keys]) => (
                  <div key={group} style={{ marginBottom: 16 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: C.primary }}>
                        {group}
                      </span>
                      <button onClick={() => toggleGroup(keys)} style={{
                        fontSize: 11, color: C.primary, background: 'none',
                        border: 'none', cursor: 'pointer', padding: 0, textDecoration: 'underline',
                      }}>
                        {keys.every(k => fields.includes(k)) ? 'Deselect all' : 'Select all'}
                      </button>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
                      {keys.map(key => (
                        <label key={key} style={{
                          display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer',
                          background: fields.includes(key) ? '#f1ebff' : C.bg,
                          border: `1.5px solid ${fields.includes(key) ? C.primary : C.border}`,
                          borderRadius: 8, padding: '5px 11px', fontSize: 12.5,
                          userSelect: 'none', transition: 'all .15s',
                        }}>
                          <input type="checkbox" checked={fields.includes(key)} onChange={() => toggle(key)}
                            style={{ accentColor: C.primary, width: 13, height: 13 }} />
                          {FIELD_LABELS[key]}
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ display: 'flex', gap: 10 }}>
                <Btn onClick={submit} disabled={creating || !mid.trim() || fields.length === 0}>
                  <Plus size={14} />
                  {creating ? 'Creating…' : `Generate KYC Link`}
                </Btn>
                <Btn onClick={onClose} color={C.muted}>Cancel</Btn>
              </div>
            </>
          ) : (
            /* Result */
            <div>
              <div style={{ textAlign: 'center', marginBottom: 20 }}>
                <div style={{ width: 56, height: 56, background: '#F0FDF4', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
                  <CheckCircle size={28} color={C.success} />
                </div>
                <h4 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 700 }}>KYC Link Generated!</h4>
                {result.merchant && (
                  <p style={{ margin: 0, fontSize: 13, color: C.muted }}>
                    For <strong>{result.merchant.name}</strong> ({result.merchant.mid})
                  </p>
                )}
              </div>

              <div style={{
                background: '#F0FDF4', border: `1px solid #BBF7D0`,
                borderRadius: 10, padding: '12px 16px',
                fontFamily: 'monospace', fontSize: 13,
                wordBreak: 'break-all', color: '#065F46', marginBottom: 14,
              }}>
                {result.url}
              </div>

              <div style={{ display: 'flex', gap: 8 }}>
                <Btn onClick={() => { navigator.clipboard.writeText(result.url); toast.success('Copied!'); }} color={C.success}>
                  <Copy size={13} /> Copy Link
                </Btn>
                <Btn onClick={() => { setResult(null); setMid(''); setFields([]); }} color={C.muted}>
                  Create Another
                </Btn>
                <Btn onClick={onClose} color={C.ink}>Close</Btn>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ─── Main Page ─── */
const AdminKYC = () => {
  const [forms, setForms]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [filterStatus, setFilter] = useState('');
  const [page, setPage]           = useState(1);
  const [meta, setMeta]           = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page };
      if (search)       params.mid    = search;
      if (filterStatus) params.status = filterStatus;
      const { data } = await adminKycAPI.list(params);
      setForms(data.data || []);
      setMeta(data);
    } catch {
      toast.error('Failed to load KYC forms');
    } finally { setLoading(false); }
  }, [page, search, filterStatus]);

  useEffect(() => { load(); }, [load]);

  const stats = {
    total:     forms.length,
    active:    forms.filter(f => f.status === 'active').length,
    submitted: forms.filter(f => f.status === 'submitted').length,
    approved:  forms.filter(f => f.status === 'approved').length,
    rejected:  forms.filter(f => f.status === 'rejected').length,
  };

  const kpiTabs = [
    { label: 'Total',     val: '',          count: meta?.total ?? forms.length, color: C.primary,  bg: C.primarySoft, Icon: FileText   },
    { label: 'Active',    val: 'active',    count: stats.active,                color: C.info,     bg: C.infoSoft,    Icon: Clock      },
    { label: 'Submitted', val: 'submitted', count: stats.submitted,             color: C.warning,  bg: C.warnSoft,    Icon: AlertCircle },
    { label: 'Approved',  val: 'approved',  count: stats.approved,              color: C.success,  bg: C.successSoft, Icon: CheckCircle },
    { label: 'Rejected',  val: 'rejected',  count: stats.rejected,              color: C.danger,   bg: C.dangerSoft,  Icon: XCircle    },
  ];

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto' }}>
      <style>{`
        .kyc-kpi-grid { display: grid; grid-template-columns: repeat(5, 1fr); gap: 14px; margin-bottom: 20px; }
        @media (max-width: 1100px) { .kyc-kpi-grid { grid-template-columns: repeat(3, 1fr); } }
        @media (max-width: 640px)  { .kyc-kpi-grid { grid-template-columns: repeat(2, 1fr); } }
      `}</style>

      {/* ── Header ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 22, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: C.ink, letterSpacing: '-.02em' }}>KYC Forms</h1>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: C.muted }}>Create and manage merchant KYC verification forms</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => { setFilter(''); setSearch(''); setPage(1); }}
            style={{ height: 38, padding: '0 14px', borderRadius: C.radiusSm, background: C.surface, color: C.body, border: `1.5px solid ${C.border}`, fontWeight: 600, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 7, fontFamily: 'inherit' }}>
            <RotateCcw size={13} /> Reset
          </button>
          <Btn onClick={() => setShowModal(true)}>
            <Plus size={15} /> Create KYC Form
          </Btn>
        </div>
      </div>

      {/* ── KPI Cards (click to filter) ── */}
      <div className="kyc-kpi-grid">
        {kpiTabs.map(k => (
          <KpiCard key={k.val} label={k.label} value={k.count} color={k.color} bg={k.bg} Icon={k.Icon}
            active={filterStatus === k.val} onClick={() => { setFilter(k.val); setPage(1); }} />
        ))}
      </div>

      {/* ── Search bar ── */}
      <div style={{ background: C.surface, border: `1.5px solid ${C.border}`, borderRadius: C.radiusSm, padding: '10px 16px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ position: 'relative', flex: 1 }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: C.muted, pointerEvents: 'none' }} />
          <input
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search by MID…"
            style={{ width: '100%', padding: '8px 12px 8px 32px', fontSize: 13, border: `1.5px solid ${C.border}`, borderRadius: C.radiusSm, outline: 'none', color: C.ink, boxSizing: 'border-box', fontFamily: 'inherit' }}
          />
        </div>
        {filterStatus && (
          <span style={{ padding: '4px 12px', borderRadius: 999, background: C.primarySoft, color: C.primary, fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
            {filterStatus.charAt(0).toUpperCase() + filterStatus.slice(1)}
            <button onClick={() => { setFilter(''); setPage(1); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.primary, padding: 0, display: 'flex' }}>×</button>
          </span>
        )}
      </div>

      {/* ── Table ── */}
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 48, color: C.muted }}>Loading…</div>
        ) : forms.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 60 }}>
            <FileText size={40} color={C.muted} style={{ marginBottom: 12 }} />
            <p style={{ color: C.muted, fontSize: 14, margin: 0 }}>No KYC forms found</p>
            <p style={{ color: C.muted, fontSize: 12, margin: '4px 0 16px' }}>Create one using the button above</p>
            <Btn onClick={() => setShowModal(true)} size="sm"><Plus size={13} /> Create KYC Form</Btn>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: C.bg, borderBottom: `1px solid ${C.border}` }}>
                  {['#','Merchant','MID','Fields','Status','Created','Actions'].map(h => (
                    <th key={h} style={{ padding: '11px 16px', textAlign: 'left', fontWeight: 600, color: C.body, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {forms.map((form, i) => (
                  <tr key={form.id} style={{ borderBottom: `1px solid ${C.border}`, transition: 'background .1s' }}
                    onMouseEnter={e => e.currentTarget.style.background = C.bg}
                    onMouseLeave={e => e.currentTarget.style.background = ''}
                  >
                    <td style={{ padding: '12px 16px', color: C.muted }}>{form.id}</td>
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ fontWeight: 600, color: C.ink }}>{form.merchant?.username || '—'}</div>
                      <div style={{ fontSize: 11, color: C.muted }}>{form.merchant?.email || ''}</div>
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <code style={{ fontSize: 11, background: C.bg, padding: '2px 7px', borderRadius: 5, color: C.body }}>
                        {form.merchant?.mid || '—'}
                      </code>
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{ background: '#f1ebff', color: C.primary, borderRadius: 20, padding: '2px 10px', fontSize: 12, fontWeight: 600 }}>
                        {(form.fields || []).length} fields
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px' }}><Badge status={form.status} /></td>
                    <td style={{ padding: '12px 16px', color: C.muted, fontSize: 12 }}>
                      {new Date(form.created_at).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' })}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <ActionMenu items={[
                        { label: 'View Details', Icon: Eye, color: C.primary, bg: C.primarySoft, onClick: () => window.location.href = `/admin/kyc/${form.id}` },
                        { label: 'Copy KYC Link', Icon: Copy, color: C.muted, bg: '#f4f4f9', onClick: () => { const url = `https://api.a1xpay.com/kyc/${form.token}`; navigator.clipboard.writeText(url); toast.success('KYC link copied!'); } },
                      ]} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {meta && meta.last_page > 1 && (
          <div style={{ padding: '12px 18px', borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13 }}>
            <span style={{ color: C.muted }}>
              Page {meta.current_page} of {meta.last_page} — {meta.total} total
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <Btn size="sm" color={C.muted} disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← Prev</Btn>
              <Btn size="sm" color={C.muted} disabled={page >= meta.last_page} onClick={() => setPage(p => p + 1)}>Next →</Btn>
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && <CreateModal onClose={() => setShowModal(false)} onCreate={() => { setShowModal(false); load(); }} />}
    </div>
  );
};

export default AdminKYC;
